import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { popupApiAdapter } from '../adapters/PopupApiAdapter';
import type { PopupFilters, CreatePopupRequest, UpdatePopupRequest } from '../models/Popup';
import { useToast } from '@hooks/useToast';
import { useTranslation } from 'react-i18next';

export function usePopupAdmin(filters?: PopupFilters) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { t } = useTranslation('popups');

  const queryKey = ['popups', 'list', filters];

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () => popupApiAdapter.getPopups(filters),
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreatePopupRequest) => popupApiAdapter.createPopup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      showSuccess(t('success.created'));
    },
    onError: () => {
      showError(t('error.create'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePopupRequest }) => popupApiAdapter.updatePopup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      showSuccess(t('success.updated'));
    },
    onError: () => {
      showError(t('error.update'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => popupApiAdapter.deletePopup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      showSuccess(t('success.deleted'));
    },
    onError: () => {
      showError(t('error.delete'));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => popupApiAdapter.archivePopup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      showSuccess(t('success.archived'));
    },
    onError: () => {
      showError(t('error.archive'));
    },
  });

  return {
    popups: data?.data ?? [],
    meta: data?.meta,
    isLoading,
    isFetching,
    error,
    refetch,
    createPopup: createMutation.mutate,
    updatePopup: updateMutation.mutate,
    updatePopupAsync: updateMutation.mutateAsync,
    deletePopup: deleteMutation.mutate,
    deletePopupAsync: deleteMutation.mutateAsync,
    archivePopup: archiveMutation.mutate,
    archivePopupAsync: archiveMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isArchiving: archiveMutation.isPending,
  };
}
